import { Box, Button, Divider, Flex, IconButton } from '@chakra-ui/react'
import { SentryErrorComponent } from '@components/Fallback'
import { FailedToLoad } from '@components/Placeholders/FailedToLoad'
import {
  useExecuteControlMessagesMutation,
  useLazyGetFullStateQuery,
} from '@features/projects/api/workflow/controlRoom'
import { ActionCommands } from '@features/projects/types/actions'
import WorkflowEditorContainer from '@features/workflow-editor/components/Container'
import { ToolbarControls } from '@features/workflow-editor/components/Toolbar'
import {
  WorkflowState,
  useWorkflowStore,
} from '@features/workflow-editor/hooks/useWorkflowStore'
import useCmxToast from '@hooks/useCmxToast'
import {
  createFileRoute,
  getRouteApi,
  Link,
  useParams,
} from '@tanstack/react-router'
import { FC } from 'react'
import {
  Workflow,
  WorkflowSessionId,
  workflowSchema,
} from '@customTypes/cloudmix/workflow'
import { ViewDropdown } from '@features/projects/components/WorkflowSessionPage/ViewDropdown'
import { WorkflowStateLoader } from '@features/projects/components/WorkflowSessionPage/SessionLoaders'
import { FaArrowsRotate, FaArrowUpRightFromSquare } from 'react-icons/fa6'
import { Route as WorkflowTemplateRoute } from '@routes/_auth/o/$orgId/projects/$projectId/workflow'
import { useProjectContext } from '@features/projects/contexts/project'
import { ExportJsonButton } from '@features/workflow-editor/components/ExportJsonButton'
import { ViewModeSwitcher } from '@features/workflow-editor/components/ViewModeSwitcher'
import { shallow } from 'zustand/shallow'

const routeApi = getRouteApi(
  '/_auth/o/$orgId/projects/$projectId/$workflowSessionId/workflow'
)

const selector = (store: WorkflowState) => ({
  resetDirty: store.resetDirty,
  readOnly: store.isReadOnly,
  getChangeSet: store.getChangeSet,
  getCurrentApiWorkflow: store.getCurrentApiWorkflow,
  validationErrors: store.validationErrors,
  isDirty: store.isDirty,
})

const useRequestWorkflowFullState = (workflowSessionId: WorkflowSessionId) => {
  const [trigger, { isFetching }] = useLazyGetFullStateQuery()

  const requestWorkflow = async () => {
    await trigger({ workflowSessionId })
  }

  return {
    requestWorkflow,
    isLoading: isFetching,
  }
}

const UpdateSessionButton: FC = () => {
  const toast = useCmxToast()
  const store = useWorkflowStore(selector, shallow)
  const params = routeApi.useParams()
  const { workflowSessionId } = params

  const { requestWorkflow } = useRequestWorkflowFullState(workflowSessionId)

  const [executeControlMessages, { isLoading: isSaving }] =
    useExecuteControlMessagesMutation()

  const hasValidationErrors = Object.values(store.validationErrors).some(
    (nodeErrors) => Object.keys(nodeErrors).length > 0
  )

  const handleSave = async () => {
    if (store.readOnly) return

    const changeSet = store.getChangeSet()
    if (!changeSet || changeSet.length === 0) {
      store.resetDirty()
      await requestWorkflow()
      toast({
        title: 'No changes to apply',
        status: 'info',
        duration: 3000,
      })
      return
    }

    const currentWorkflow = store.getCurrentApiWorkflow()
    const validation = workflowSchema.safeParse(currentWorkflow)
    if (!validation.success) {
      console.warn('Workflow validation failed:', validation.error.errors)
      const invalidComponentCount = new Set(
        validation.error.errors
          .filter(
            (e) => e.path[0] === 'components' && typeof e.path[1] === 'number'
          )
          .map((e) => e.path[1])
      ).size
      toast({
        title: 'Workflow has validation errors',
        description:
          invalidComponentCount > 0
            ? `${invalidComponentCount} component${invalidComponentCount === 1 ? ' has' : 's have'} errors. Select ${invalidComponentCount === 1 ? 'it' : 'them'} to see details.`
            : 'Please review the workflow configuration.',
        status: 'warning',
        duration: 5000,
      })
      return
    }

    try {
      await executeControlMessages({
        workflowSessionId,
        actionCommands: changeSet as ActionCommands[],
      }).unwrap()

      toast({
        title: 'Workflow updated successfully',
        status: 'success',
        duration: 3000,
      })
    } catch (err) {
      toast({
        title: err?.error || 'Error updating workflow',
        description: err?.cause || 'An unexpected error occurred.',
        status: 'error',
        duration: 3000,
      })
    }
    await requestWorkflow()
  }

  return (
    <Button
      onClick={handleSave}
      isDisabled={
        !store.isDirty || isSaving || hasValidationErrors || store.readOnly
      }
      isLoading={isSaving}
      loadingText="Saving..."
      colorScheme="blue"
    >
      Apply Changes
    </Button>
  )
}

const SyncWorkflowButton: FC = () => {
  const params = routeApi.useParams()
  const { workflowSessionId } = params

  const { requestWorkflow, isLoading } =
    useRequestWorkflowFullState(workflowSessionId)

  const handleSync = async () => {
    await requestWorkflow()
  }

  return (
    <IconButton
      onClick={handleSync}
      isLoading={isLoading}
      variant="ghost"
      icon={<FaArrowsRotate />}
      aria-label="Reset Workflow"
    />
  )
}

const LaunchTemplateLinkButton: FC = () => {
  const { projectId, orgId } = useParams({
    from: '/_auth/o/$orgId/projects/$projectId/$workflowSessionId/workflow',
  })

  return (
    <Link to={WorkflowTemplateRoute.to} params={{ orgId, projectId }}>
      <Button
        size="sm"
        colorScheme="primary"
        variant="outline"
        rightIcon={<FaArrowUpRightFromSquare />}
      >
        Edit Launch Template
      </Button>
    </Link>
  )
}

type WorkflowEditorContentProps = {
  workflow: Workflow
}

const WorkflowEditorContent: FC<WorkflowEditorContentProps> = ({
  workflow,
}) => {
  const params = routeApi.useParams()
  const { workflowSessionId, projectId } = params
  const { format } = useProjectContext()

  if (!format) {
    return <FailedToLoad message="Failed to load workflow session." />
  }

  return (
    <>
      <Flex justify="space-between" align="center" mb={2} wrap="wrap">
        <ViewDropdown workflowSessionId={workflowSessionId}></ViewDropdown>
        <ViewModeSwitcher />
      </Flex>
      <Divider />

      <Box py="2" overflowX="auto" h="92%">
        <WorkflowEditorContainer
          projectId={projectId}
          workflow={workflow}
          mediaFormat={format}
          renderToolbar={({
            addComponent,
            undoRedo,
            createMacro,
            statusBadges,
          }: ToolbarControls) => (
            <Flex gap={2} alignItems="center" mb={1} wrap="wrap">
              <SyncWorkflowButton />
              <UpdateSessionButton />
              {statusBadges}
              <Box flex={1} />
              {addComponent}
              {undoRedo}
              {createMacro}
              <ExportJsonButton />
              <Box flex={1} />
              <LaunchTemplateLinkButton />
            </Flex>
          )}
        />
      </Box>
    </>
  )
}

const WorkflowRoute: FC = () => {
  const { workflowSessionId } = routeApi.useParams()

  return (
    <WorkflowStateLoader workflowSessionId={workflowSessionId} forceRefresh>
      {(workflow) => <WorkflowEditorContent workflow={workflow} />}
    </WorkflowStateLoader>
  )
}

export const Route = createFileRoute(
  '/_auth/o/$orgId/projects/$projectId/$workflowSessionId/workflow'
)({
  component: WorkflowRoute,
  beforeLoad: () => ({ getTitle: () => 'Workflow - Active Session' }),
  errorComponent: SentryErrorComponent,
  loader: () => ({
    crumb: 'Workflow',
  }),
})
